import dayjs, {Dayjs} from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import updateLocale from 'dayjs/plugin/updateLocale';
import weekOfYear from "dayjs/plugin/weekOfYear";

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(updateLocale);
dayjs.extend(weekOfYear);
dayjs.updateLocale('es', {
    weekStart: 4,
});

export const FIRST_DAY_OF_WEEK = 4;//Jueves
export const LAST_DAY_OF_WEEK = 3;//Miércoles

/**
 * Obtiene el primer día de la semana actual según la configuración del inicio de semana.
 *
 * @function getFirstDayOfCurrentWeek
 * @returns {Dayjs} - Objeto `Dayjs` que representa el primer día de la semana actual,
 *                    ajustado al inicio del día (00:00:00).
 *
 * @description
 * Esta función devuelve la fecha correspondiente al **primer día de la semana actual**
 * tomando como referencia la fecha actual del sistema (`dayjs()`).
 *
 * Internamente, delega el cálculo a la función {@link toFirstDayOfWeek},
 * que determina el inicio de la semana según el valor configurado
 * en la constante global `FIRST_DAY_OF_WEEK`.
 *
 * @example
 * // Si hoy es viernes 25 de octubre de 2025 y FIRST_DAY_OF_WEEK = 1 (lunes)
 * const firstDay = getFirstDayOfCurrentWeek();
 * console.log(firstDay.format("YYYY-MM-DD")); // → "2025-10-20"
 *
 * @example
 * // Si FIRST_DAY_OF_WEEK = 4 (jueves)
 * const firstDay = getFirstDayOfCurrentWeek();
 * console.log(firstDay.format("YYYY-MM-DD")); // → "2025-10-23"
 *
 * @note
 * - Requiere que la función `toFirstDayOfWeek(date: Dayjs)` esté definida en el mismo módulo o importada.
 * - La constante `FIRST_DAY_OF_WEEK` debe existir y definir qué día se considera el inicio de la semana.
 * - Devuelve una nueva instancia de `Dayjs` (no modifica la fecha original).
 */
export function getFirstDayOfCurrentWeek(): Dayjs {
    return toFirstDayOfWeek(dayjs());
}

/**
 * Devuelve el primer día de la semana correspondiente a una fecha dada,
 * considerando un día configurable como inicio de semana.
 *
 * @function toFirstDayOfWeek
 * @param {Dayjs} date - Fecha base desde la cual se calculará el primer día de la semana.
 * @returns {Dayjs} - Objeto `Dayjs` que representa el primer día de la semana,
 *                    ajustado al inicio del día (00:00:00).
 *
 * @description
 * Esta función calcula el primer día de la semana que contiene la fecha especificada.
 * Se basa en la constante global `FIRST_DAY_OF_WEEK`, que define cuál es el día
 * considerado como el inicio de semana (por ejemplo, `0` para domingo, `1` para lunes, `4` para jueves, etc.).
 *
 * La lógica:
 * - Si el día actual (`date.day()`) es **mayor** que `FIRST_DAY_OF_WEEK`, se resta la diferencia
 *   entre ambos para retroceder hasta el primer día de esa misma semana.
 * - Si el día actual es **menor**, significa que ya está en una semana cuyo inicio
 *   pertenece a la semana anterior; se ajusta restando el exceso de días necesario.
 * - Si el día actual coincide con `FIRST_DAY_OF_WEEK`, simplemente se devuelve el mismo día.
 *
 * En todos los casos, la hora se normaliza a la medianoche usando `startOf("day")`.
 *
 * @example
 * // Suponiendo FIRST_DAY_OF_WEEK = 1 (lunes)
 * const date = dayjs("2025-10-24"); // Viernes
 * const result = toFirstDayOfWeek(date);
 * console.log(result.format("YYYY-MM-DD")); // → "2025-10-20" (lunes)
 *
 * @example
 * // Si FIRST_DAY_OF_WEEK = 4 (jueves)
 * const date = dayjs("2025-10-26"); // Domingo
 * const result = toFirstDayOfWeek(date);
 * console.log(result.format("YYYY-MM-DD")); // → "2025-10-23" (jueves)
 *
 * @note
 * - La constante `FIRST_DAY_OF_WEEK` debe existir en el mismo ámbito donde se defina la función.
 * - Asegúrese de haber cargado los plugins necesarios de Dayjs si usa operaciones de semana (`weekOfYear`, `isoWeek`, etc.).
 * - La función **no altera el objeto original**, sino que devuelve uno nuevo.
 */
export function toFirstDayOfWeek(date: Dayjs): Dayjs {
    const day = date.day(); // 0=Sun .. 6=Sat
    let offset = day - FIRST_DAY_OF_WEEK;
    if (offset < 0) offset += 7;
    // Build the target date from components — DST-safe (no millisecond arithmetic)
    return dayjs(new Date(date.year(), date.month(), date.date() - offset)).startOf("day");
}

/**
 * Retorna el primer día de la semana anterior según el día base indicado.
 *
 * @function toPreviousFirstDayOfWeek
 * @param {Dayjs} date - Fecha base desde la cual se calculará el inicio de la semana anterior.
 * @returns {Dayjs} - Objeto `Dayjs` que representa el primer día de la semana anterior,
 *                    ajustado al inicio del día (00:00:00).
 *
 * @description
 * Esta función determina cuál fue el primer día de la semana anterior
 * en relación con una fecha dada (`date`).
 *
 * - Utiliza la propiedad `date.day()` para obtener el índice del día actual
 *   (donde 0 = domingo, 1 = lunes, …, 6 = sábado).
 * - Si la fecha ya coincide con el `FIRST_DAY_OF_WEEK` (por ejemplo, lunes o jueves, según su configuración),
 *   retorna la fecha misma al inicio del día.
 * - En caso contrario, retrocede la cantidad de días necesaria para llegar
 *   al primer día de la semana anterior.
 *
 * @example
 * // Suponiendo que FIRST_DAY_OF_WEEK = 1 (lunes)
 * const current = dayjs("2025-10-25"); // Sábado
 * const result = toPreviousFirstDayOfWeek(current);
 * console.log(result.format("YYYY-MM-DD")); // -> "2025-10-13" (lunes anterior)
 *
 * @note
 * - La constante `FIRST_DAY_OF_WEEK` debe definirse en el contexto,
 *   e indica qué día se considera como el inicio de la semana (0–6).
 * - La función usa `startOf("day")` para normalizar la hora a medianoche.
 */
function toPreviousFirstDayOfWeek(date: Dayjs): Dayjs {
    const dayOfWeek = date.day(); // 0=domingo, ..., 6=sábado
    let previousDayOfWeek = date.startOf("day");
    if (dayOfWeek === FIRST_DAY_OF_WEEK) {
        return previousDayOfWeek;
    }
    return previousDayOfWeek.subtract(dayOfWeek + 1, "day");
}

/**
 * Calcula el número de semana personalizada donde la semana comienza en un día distinto al estándar ISO-8601.
 *
 * @function getWeekNumber
 * @param {Dayjs} date - Fecha base para la cual se desea obtener el número de semana personalizado.
 * @returns {number} - Número de semana correspondiente a la fecha dada, considerando el primer día configurado (`FIRST_DAY_OF_WEEK`).
 *
 * @description
 * Esta función ajusta el cálculo del número de semana tomando en cuenta un **inicio de semana personalizado**, distinto al utilizado por defecto en ISO-8601.
 *
 * Por ejemplo, en este sistema:
 * - La semana comienza en **jueves** (`FIRST_DAY_OF_WEEK = 4`).
 * - La semana termina en **miércoles**.
 *
 * En el estándar ISO-8601, la semana comienza en **lunes** y puede provocar
 * que los últimos días de diciembre pertenezcan a la **semana 1 del siguiente año**.
 * En este esquema personalizado, se recalcula el número de semana para reflejar correctamente ese desplazamiento.
 *
 * ### Lógica:
 * 1. Se obtiene el número de semana según ISO (`date.week()`).
 * 2. Si el día actual (`date.day()`) ocurre **antes** del `FIRST_DAY_OF_WEEK`,
 *    la fecha pertenece realmente a la semana anterior.
 * 3. En ese caso, se usa `toPreviousFirstDayOfWeek(date)` para obtener el número de semana correcto.
 *
 * ### Ejemplo práctico
 * Suponga que la semana comienza en **jueves (FIRST_DAY_OF_WEEK = 4)**:
 *
 * - En el sistema ISO:
 *   - **29–31 diciembre 2024** pertenecen a la **semana 1 del 2025**.
 * - En el sistema personalizado (semana jueves–miércoles):
 *   - **26–31 diciembre 2024** y **1 enero 2025** pertenecen a la **semana 1 del 2025**.
 *
 * ```ts
 * // Ejemplo con FIRST_DAY_OF_WEEK = 4 (jueves)
 * const date = dayjs("2024-12-29"); // Domingo
 * const week = getWeekNumber(date);
 * console.log(week); // → 1
 * ```
 *
 * @note
 * - Requiere que `FIRST_DAY_OF_WEEK` esté definido globalmente (por ejemplo, `const FIRST_DAY_OF_WEEK = 4`).
 * - Depende de las funciones auxiliares:
 *   - {@link toPreviousFirstDayOfWeek} para ajustar el cálculo cuando la fecha cae antes del inicio de semana.
 * - Es importante que Dayjs tenga cargado el plugin `weekOfYear` (`dayjs.extend(weekOfYear)`).
 * - Devuelve un número entero correspondiente al número de semana dentro del año.
 */
export function getWeekNumber(date: Dayjs): number {
    const thursday = toFirstDayOfWeek(date);
    const year = thursday.year();
    const jan1 = dayjs(`${year}-01-01`).startOf('day');
    const thursdayOfWeek1 = toFirstDayOfWeek(jan1);
    // Use Date.UTC so DST transitions don't corrupt the day count
    const thursdayUTC = Date.UTC(thursday.year(), thursday.month(), thursday.date());
    const week1UTC = Date.UTC(thursdayOfWeek1.year(), thursdayOfWeek1.month(), thursdayOfWeek1.date());
    const diffDays = (thursdayUTC - week1UTC) / 86400000;
    return Math.floor(diffDays / 7) + 1;
}

export function getDateRange(
    startDate: Dayjs,
    endDate: Dayjs,
    format: string = "YYYY-MM-DD"
): string {
    return `${startDate.format(format)} / ${endDate.format(format)}`;
}

/**
 * Inverso de getWeekNumber: devuelve el Jueves (FIRST_DAY_OF_WEEK) que
 * ancla la semana N del año dado, bajo el calendario Jueves-anclado.
 *
 * Estrategia: Jan 4 siempre cae dentro de la semana 1 del año en
 * calendarios anclados al jueves (incluso cuando Jan 1 cae en Vie/Sab/Dom
 * y por convención pertenece a la última semana del año previo).
 */
export function getStartOfWeekByNumber(year: number, weekNumber: number): Dayjs {
    const jan1 = dayjs(`${year}-01-01`).startOf('day');
    const w1Thursday = toFirstDayOfWeek(jan1);
    // Build the target date from components so DST never shifts midnight
    const daysToAdd = (weekNumber - 1) * 7;
    const target = new Date(w1Thursday.year(), w1Thursday.month(), w1Thursday.date() + daysToAdd);
    return dayjs(target).startOf('day');
}
